import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Modal } from 'shared/components/ui/modal';
import { Button } from 'shared/components/ui/button';

import { getSportColor, getSportOnColor } from 'shared/utils/sport-colors';
import { useMarketTypesQuery, useScheduleRtpPreferencesQuery } from '../queries';
import { getMarketWinDescriptionKey } from '../marketWinDescriptionKey';

type Props = {
  open: boolean;
  onClose: () => void;
  scheduleId: string | number | null | undefined;
  sportId?: number | string | null | undefined;
};

export function RtpTransparencyModal({ open, onClose, scheduleId, sportId }: Props) {
  const { t } = useTranslation();

  const prefsQuery = useScheduleRtpPreferencesQuery({ scheduleId });
  const marketTypesQuery = useMarketTypesQuery();

  const prefs = prefsQuery.data ?? [];
  const marketTypes = marketTypesQuery.data ?? [];

  const sportIdNum = sportId != null ? Number(sportId) : null;
  const sportColor = sportIdNum != null ? getSportColor(sportIdNum) : null;
  const sportOnColor = sportIdNum != null ? getSportOnColor(sportIdNum) : null;

  const rows = React.useMemo(() => {
    const mtById = new Map<string, any>();
    for (const mt of marketTypes) {
      mtById.set(String(mt.market_type_id), mt);
    }

    return prefs
      .map((p) => {
        const mt = mtById.get(String(p.market_type_id));
        const effectiveSportId = mt?.sport_id ?? (p as any)?.sport_id ?? null;
        if (sportIdNum != null && effectiveSportId != null && Number(effectiveSportId) !== sportIdNum) return null;
        return {
          market_type_id: p.market_type_id,
          name: mt?.name ?? (p as any)?.market_type_name ?? String(p.market_type_id),
          sport_id: effectiveSportId,
          market_type_group_id: mt?.market_type_group_id ?? null,
          rtp_value: p.rtp_value,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => String(a.name).localeCompare(String(b.name)));
  }, [prefs, marketTypes, sportIdNum]);

  const isLoading = prefsQuery.isLoading || marketTypesQuery.isLoading;
  const hasError = prefsQuery.isError || marketTypesQuery.isError;

  const content = (
    <div className="space-y-4">
      {hasError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
          {t('ERROR_LOADING_DATA') || 'Failed to load RTP data.'}
        </div>
      )}

      <div
        className="c7-panel overflow-hidden rounded-lg border border-border"
        style={
          sportColor && sportOnColor
            ? ({
                '--sport-color': sportColor,
                '--sport-on-color': sportOnColor,
                borderLeftWidth: '4px',
                borderLeftStyle: 'solid',
                borderLeftColor: sportColor,
              } as React.CSSProperties)
            : undefined
        }
      >
        <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-border bg-muted/40">
          <div className="text-sm font-semibold">{t('MARKETS') || 'Markets'}</div>
          <div className="text-xs font-semibold text-muted-foreground">
            {t('RTP') || 'RTP'}%
          </div>
        </div>

        <div className="divide-y divide-border">
          {isLoading ? (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-4 text-sm text-muted-foreground">
              {t('NO_RTP_DATA') || 'No RTP data available for this schedule.'}
            </div>
          ) : (
            rows.map((r: any) => (
              <div key={String(r.market_type_id)} className="flex items-center justify-between gap-4 px-4 py-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {t(getMarketWinDescriptionKey(r))}
                  </div>
                </div>
                <div className="shrink-0 text-sm font-bold tabular-nums">
                  {Number.isFinite(Number(r.rtp_value)) ? Number(r.rtp_value).toFixed(2) : String(r.rtp_value)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('RTP_TRANSPARENCY_TITLE') || 'Winning probability (RTP)'}
      description={
        t('RTP_TRANSPARENCY_DESC') ||
        'RTP (Return to Player) is the expected percentage of stakes returned to players over time. It is not a guarantee on a single bet.'
      }
      size="lg"
      footer={
        <Button variant="sport" onClick={onClose}>
          {t('OK') || 'OK'}
        </Button>
      }
    >
      <div
        style={
          sportColor && sportOnColor
            ? ({ '--sport-color': sportColor, '--sport-on-color': sportOnColor } as React.CSSProperties)
            : undefined
        }
      >
        {content}
      </div>
    </Modal>
  );
}

